/** The three pieces of what a failed write says when the service answers 429 or 503; the failure copy table reuses them, so there is one wording. */
export const BUSY_TITLE = 'The service is busy';
export const BUSY_RETRY_HINT = 'Try again in a moment.';
export const CHECK_LIST_HINT = 'Check the list before trying again.';

/** What the create forms say when the service answers 429 (rate limited) or 503 (unavailable): nothing is wrong with the form. */
export const BUSY_MESSAGE = `${BUSY_TITLE}. ${BUSY_RETRY_HINT}`;

/** The HTTP status of a failed request: axios puts it on `response`, the Kubernetes client's own `Error` on `status`. */
export function statusOf(error: unknown): number | undefined {
  const e = error as { response?: { status?: number }; status?: number } | null | undefined;
  return e?.response?.status ?? e?.status;
}

/** A 503 may have been answered after the request went through: the person looks before sending it again. */
export const UNAVAILABLE_MESSAGE = `${BUSY_TITLE}. ${CHECK_LIST_HINT}`;

/** What the alert says: 429 refused the request, so retrying is safe; 503 may have created it already. */
export function busyMessage(error: unknown): string {
  return statusOf(error) === 503 ? UNAVAILABLE_MESSAGE : BUSY_MESSAGE;
}

/** The error is the service saying it is busy or down for now (429, 503): a retry may succeed, a changed form is not needed. */
export function isServiceBusy(error: unknown): boolean {
  const status = statusOf(error);
  return status === 429 || status === 503;
}

/** What Retry does: a 429 was refused, so it posts again; a 503 may have been created, so it looks at the list first. */
export function retryAction(error: unknown): 'post' | 'check-list' {
  return statusOf(error) === 503 ? 'check-list' : 'post';
}

/** What the refetched list says about the thing the person tried to create. */
export type ListCheck = { kind: 'found'; name: string } | { kind: 'absent'; name: string };

/** Whether `name` is in the list that was just read (same name, ignoring case and surrounding spaces). */
export function checkListed(items: readonly { name?: string | null }[], name: string): ListCheck {
  const wanted = name.trim().toLowerCase();
  const found = wanted !== '' && items.some((i) => (i.name ?? '').trim().toLowerCase() === wanted);
  return { kind: found ? 'found' : 'absent', name: name.trim() };
}

export const CREATE_ANYWAY = 'Create anyway';

/** The answer after a 503 once the list was read: the earlier request went through, or it did not. */
export function listCheckMessage(check: ListCheck): string {
  return check.kind === 'found'
    ? `“${check.name}” is already in your list, so the earlier request went through. Create another one anyway?`
    : `“${check.name}” is not in your list. Create anyway?`;
}

/** The name in the request that was SENT (`mutation.variables`), not the field as it reads now: the person may have edited it since. */
export function sentName(variables: unknown, key: 'instance' | 'cluster'): string {
  const name = (variables as Record<string, { name?: unknown } | undefined> | null | undefined)?.[key]?.name;
  return typeof name === 'string' ? name : '';
}
